import React, { useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from '@reach/router';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import _ from 'lodash';
import { orgId, contactIdPrivate, setPageTitle } from 'utils';
import * as signUpSelectors from 'state/selectors/signUpSelectors';
import * as signUpActions from 'state/actions/signUpActions';
import * as locationActions from 'state/actions/locationActions';
import * as locationSelectors from 'state/selectors/locationSelectors';
import getProfileValidationSchema from 'components/forms/my-profile/getNewValidationSchema';
import getContactValidationSchema from 'components/forms/contact-information/getValidationSchema';
import getLocationValidationSchema from 'components/forms/location-information/getValidationSchema';
import getValidationSchemaForDropDown from 'components/forms/profile-form/validationSchemaForDropdown';
import ReactGA from 'react-ga';
import { I18N, I18NConsumer, GlobalContext } from 'commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import v from 'scss/v';
import MyProfileSection from './my-profile';
import ContactInformationSection from './contact-information';
import LocationInformationSection from './location-information';
import AcceptInformation from './AcceptInformation';
import onSignUpSubmit from './onSignUpSubmit';
import TopErrorBox from './SignUpErrorBox'
import { PREFERREDLANGUAGENOTPROVIDED } from 'constants/applicationConstant';
import { set } from 'mongoose';

const SignUpForm = ({
  addresses,
  verifyLocation,
  createAccountForPublicOrg,
  firstName,
  lastName,
  emailInfo,
  phoneInfo,
  preferredLanguage,
  preferredLanguageSettings,
  PhoneValidate,
  locationAddress,
  routerState,
  emailQuietTimeFrame,
  phoneQuietTimeFrame,
  signPrivateUserInfoError,
  fetchCountries,
  countryData,
  fetchPublicProfile,
  publicProfileId,
  countryCode,
  individualAccountId,
  SMSInfo,
  slackInfo,
  slackQuietTimeFrame,
  smsQuietTimeFrame,
  SmsValidate,
  fetchLanguageSettings
}) => {
  const {
    lang,
    fetchInitData,
    organization: {
      orgName, showAddress,
      signupEmailMandatory, SMSSetting = {}, phoneSetting = {}, userStatus,
      defaultCountry: defaultCountryCode = 'US', isFedRAMPCustomer,
      addressSetting: {
        mandatory: addressMandatory = false,
        editable: addresseEditable = false,
        defaultAddressGeoCodingSource: addressGeoCodingSource,
      },
      minSignup,
      isEnableCountryCodeForMemberPortal, slackSetting = {},
      shouldShowPreferredLanguage
    },
    setIsPublicPortalEnabled,
  } = useContext(GlobalContext);
  const combinePhoneSetting = useRef();
  const combineEmailSetting = useRef();
  const combineSMSSetting = useRef();
  const combineSlackSetting = useRef();
  const [isDeleteOperate, setIsDeleteOperate] = useState(false);
  const [isAddressVerifyPass, setIsAddressVerifyPass] = useState(!addresseEditable);
  const [isShowAddressVerifyTip, setShowAddressVerifyTip] = useState(false);
  const [isHasPrivateLocation, setIsHasPrivateLocation] = useState(false);
  const [isEditLocation, setIsEditLocation] = useState(false);
  const [showMiniRequiredContactPathError, updateShowMiniRequiredContactPathError] = useState(false);
  const [isLangChange, setIsLangChange] = useState(false);
  const [iscallOutMsgShow, setIscallOutMsgShow] = useState(false);
  const [formatCountries, setFormatCountries] = useState([]);
  const [formatSelectedCountries, setFormatSelectedCountries] = useState([]);
  const [hasUnsupportedCountrycode, setHasUnsupportedCountrycode] = useState({});
  const profileValidationSchema = getProfileValidationSchema({ userStatus, isFedRAMPCustomer });
  const contactPathLength = 1 + (!_.isEmpty(SMSSetting) && SMSSetting.exposed) + (!_.isEmpty(phoneSetting) && phoneSetting.exposed) + (!_.isEmpty(slackSetting) && slackSetting.exposed);
  const minContactPath = contactPathLength < minSignup ? contactPathLength : minSignup;
  const isAllFieldMandatory = minContactPath === contactPathLength;
  const emailMandatory = signupEmailMandatory;
  const phoneMandatory = phoneSetting.mandatory;
  const SMSMandatory = isAllFieldMandatory || SMSSetting.mandatory;
  const slackMandatory = slackSetting.mandatory;
  const contactValidationSchema = getContactValidationSchema({
    emailMandatory,
    phoneMandatory,
    SMSMandatory,
    slackMandatory
  });
  const locationValidationSchema = getLocationValidationSchema({
    mandatory: addressMandatory,
    oldLocationName: addresses.length ? addresses[0].locationName : ''
  });
  const preferredLanguageSchema = shouldShowPreferredLanguage ? getValidationSchemaForDropDown({ 
    isFieldMandatory: isPreferredLanguageMandatory && isPreferredLanguageEditable,
    fieldName: 'preferredLanguage',
    fieldErrorString: `ssp.register.preferredLanguage`,
    defaultString: [PREFERREDLANGUAGENOTPROVIDED]
  }) : {};

  const initialLang = useRef(lang);
  const {
    languages: preferredLanguageList = [],
    editable: isPreferredLanguageEditable,
    mandatory: isPreferredLanguageMandatory,
    exposed: isPreferredLanguageExposed,
  } = preferredLanguageSettings;

  useEffect(() => {
    fetchPublicProfile();
    fetchCountries();
    fetchLanguageSettings();
    ReactGA.pageview(`/newRegistration/signUpPage/${orgId}`);
  }, []);

  useEffect(() => {
    if (lang !== initialLang.current) {
      setIsLangChange(true);
    }
  }, [lang]);

  useEffect(() => {
    if (!_.isEmpty(countryData)) {
      const countriesTmp = countryData.countries.map(item => {
        const newItem = {
          ...item,
          iso2: _.lowerCase(item.code)
        };
        return newItem;
      });
      setFormatCountries([...countriesTmp]);

      const selectedCountriesTmp = countryData.selectedCountries.map(item => {
        const newItem = {
          ...item,
          iso2: _.lowerCase(item.code)
        };
        return newItem;
      });
      setFormatSelectedCountries([...selectedCountriesTmp]);
      if (isEnableCountryCodeForMemberPortal) {
        if (phoneSetting.editable && phoneInfo && selectedCountriesTmp.findIndex(item =>
          item.code.toLowerCase() === countryCode.toLowerCase()) === -1) {
          hasUnsupportedCountrycode.mobilePhone = true;
          setHasUnsupportedCountrycode({ ...hasUnsupportedCountrycode });
        }
        if (SMSSetting.editable && SMSInfo.value && selectedCountriesTmp.findIndex(item =>
          item.code.toLowerCase() === SMSInfo.countryCode.toLowerCase()) === -1) {
          hasUnsupportedCountrycode.smsPhone = true;
          setHasUnsupportedCountrycode({ ...hasUnsupportedCountrycode });
        }
      }
    }
  }, [countryData, phoneInfo, SMSInfo]);

  useEffect(() => {
    if (!_.isEmpty(addresses)) {
      const [address] = addresses;
      const privateAddress = {
        city: address.city || '',
        country: address.country,
        countryName: '',
        locationName: address.locationName || '',
        position: {
          lat: address.lat,
          lng: address.lon,
        },
        lat: address.lat,
        lng: address.lon,
        postalCode: address.postalCode,
        state: address.state,
        street: address.streetAddress,
        suite: address.suite,
      };
      setIsHasPrivateLocation(true);
      verifyLocation({
        addresses: [{ ...privateAddress, countryName: '' }],
      });
    }
  }, [addresses]);

  if (!_.isEmpty(emailInfo)) {
    combineEmailSetting.current = { ...emailInfo, noValidate: emailInfo.noValidate, exposed: true };
  } else {
    combineEmailSetting.current = { noValidate: false };
  }
  if (!_.isEmpty(phoneInfo)) {
    combinePhoneSetting.current = { ...phoneSetting, noValidate: phoneInfo.noValidate };
  } else {
    combinePhoneSetting.current = { ...phoneSetting, noValidate: false };
  }
  if (!_.isEmpty(SMSInfo)) {
    combineSMSSetting.current = { ...SMSSetting, noValidate: SMSInfo.noValidate };
  } else {
    combineSMSSetting.current = { ...SMSSetting, noValidate: false };
  }
  if (!_.isEmpty(slackInfo)) {
    combineSlackSetting.current = { ...slackInfo, noValidate: slackInfo.noValidate, exposed: true };
  } else {
    combineSlackSetting.current = { noValidate: false };
  }

  const { fromSSO } = routerState || {};
  if (individualAccountId) {
    return <Redirect noThrow to={`/${orgId}/new/account/profile`} />;
  }

  const modifiedPreferredLanguage = preferredLanguageList ?
    Object.keys(preferredLanguageList).map((value) => ({
      id: value,
      displayLabel: preferredLanguageList[value],
    })).filter((item) => item.id !== 'eb_empty') : [];

  const defaultProfileValues = {
    firstName: firstName || '',
    lastName: lastName || '',
    publicProfileId: publicProfileId || '',
    preferredLanguage: modifiedPreferredLanguage.findIndex(lang => lang.id === preferredLanguage) !== -1 ?
      preferredLanguage : PREFERREDLANGUAGENOTPROVIDED,
  };

  const defaultContactValues = {
    email: emailInfo || '',
    mobilePhoneCountryCode: countryCode ? countryCode : defaultCountryCode,
    mobilePhone: phoneInfo ? phoneInfo : '',
    smsPhoneCountryCode: countryCode ? countryCode : defaultCountryCode,
    smsPhone: phoneInfo ? phoneInfo : '',
    slack: slackInfo.value ? slackInfo.value : '',
    country: defaultCountryCode,
  };

  let defaultLocationValues;
  let pristineLocationName;

  return (
    <I18NConsumer>
      {(i18n) => {
        setPageTitle(`${orgName} - ${i18n.get('ssp.register.title')}`);

        const defaultLocationName = '';
        const nullLocationValues = {
          locationName: defaultLocationName,
          street: '',
          suite: '',
          city: '',
          postalCode: '',
          state: '',
          country: defaultCountryCode,
        };
        if (addresses.length) {
          const [address] = addresses;
          defaultLocationValues = {
            locationName: address.locationName || '',
            street: address.streetAddress || '',
            suite: address.suite || '',
            city: address.city || '',
            postalCode: address.postalCode || '',
            state: address.state || '',
            country: address.country || defaultCountryCode,
            id: address.id || '',
            lat: address.lat || '',
            lon: address.lon || ''
          };
          pristineLocationName = address.locationName;
        } else {
          defaultLocationValues = {
            locationName: defaultLocationName,
            street: '',
            suite: '',
            city: '',
            postalCode: '',
            state: '',
            country: defaultCountryCode,
          };
        }
        return (
          <div className="content-box">
            <div className="p-3 form-text" id="registrationComment">
              <I18N>form.fields.are.required</I18N>
            </div>
            <Formik
              enableReinitialize={!isLangChange}
              initialValues={{
                ...defaultProfileValues,
                ...defaultContactValues,
                ...defaultLocationValues,
                accept: false
              }}
              validationSchema={() => yup.object().shape({
                ...profileValidationSchema,
                ...contactValidationSchema,
                ...locationValidationSchema,
                ...preferredLanguageSchema,
              })
              }
              onSubmit={(values, { setSubmitting, setFieldError }) => {
                if (addressMandatory || addresses.length) {
                  if (!isAddressVerifyPass) {
                    setShowAddressVerifyTip(true);
                    setFieldError('location', 'ssp.map.verify.your.location.errormsg')
                    setSubmitting(false)
                    return;
                  }
                } else if (values.street || values.locationName) {
                  if (!isAddressVerifyPass) {
                    setShowAddressVerifyTip(true);
                    if (values.street && values.locationName) {
                      setFieldError('location', 'ssp.map.verify.your.location.errormsg')
                    } else {
                      setFieldError('location', 'ssp.map.verify.your.location.errormsg.required')
                    }
                    setSubmitting(false)
                    return;
                  }
                } else if (values.preferredLanguage === PREFERREDLANGUAGENOTPROVIDED && isPreferredLanguageMandatory && isPreferredLanguageEditable) {
                  setFieldError('preferredLanguage', 'Preferred language is required.');
                  setSubmitting(false);
                  return;
                }

                const { smsPhone, mobilePhone, email, slack } = values;

                const effectiveContactPaths = [smsPhone, mobilePhone, email, slack].filter(value => !!value);
                if (effectiveContactPaths.length < minContactPath) {
                  updateShowMiniRequiredContactPathError(true);
                  setFieldError('ContactInformation', `new.registration.signup.create.contactpath.error, ${minContactPath}`)
                  setSubmitting(false)
                  return;
                }
                updateShowMiniRequiredContactPathError(false);
                setFieldError('ContactInformation', '')
                if (publicProfileId && publicProfileId.trim() !== "") {
                  setIsPublicPortalEnabled(true);
                  sessionStorage.setItem("isPublicPortalEnabled", true);
                }
                onSignUpSubmit({
                  values,
                  publicProfileId,
                  setSubmitting,
                  locationAddress,
                  phoneSetting: combinePhoneSetting.current,
                  emailSetting: combineEmailSetting.current,
                  SMSSetting: combineSMSSetting.current,
                  slackSetting: combineSlackSetting.current,
                  addressGeoCodingSource,
                  createAccountForPublicOrg: async (data, callback) => {
                    try {
                      await createAccountForPublicOrg(data, callback, fetchInitData);
                    } catch (error) {
                      setSubmitting(false);
                      setFieldError('submit', error.message);
                    }
                  },
                  SmsValidate,
                  PhoneValidate,
                  pristineLocationName,
                  emailQuietTimeFrame,
                  phoneQuietTimeFrame,
                  smsQuietTimeFrame,
                  slackQuietTimeFrame
                });
              }
              }
            >
              {({
                values,
                touched,
                errors,
                validateField,
                handleSubmit,
                isSubmitting,
                setValues,
                setFieldValue,
                setFieldError,
                setFieldTouched,
                handleBlur,
                setSubmitting
              }) => {
                const [errorsRef, setErrorsRef] = useState({});
                useEffect(() => {
                    let isMounted = true;
                    if (isMounted) {
                      setErrorsRef((prevErrorsRef) => {
                        const merged = { ...prevErrorsRef };
                        Object.keys(merged).forEach((key) => {
                          const isSmsPhone = key === 'smsPhone' || key === 'mobilePhone';
                          if (!isSmsPhone && !errors[key]) {
                            delete merged[key];
                          }
                        });
                        return { ...merged, ...errors };
                      });
                    }

                  return () => {
                    isMounted = false;
                  }
                }, [errors]);

                return (
                  <form>
                    <TopErrorBox
                      errors={errors}
                      isSubmitting={isSubmitting}
                      outerStyle="col-md-8"
                      innerStyle="mb-3"
                    />
                    <MyProfileSection
                      resetLocationValues={() => setValues({ ...values })}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      setFieldError={setFieldError}
                      preferredLanguage={defaultProfileValues.preferredLanguage}
                      preferredLanguageList={modifiedPreferredLanguage}
                      isPreferredLanguageEditable={isPreferredLanguageEditable}
                      isPreferredLanguageMandatory={isPreferredLanguageMandatory}
                      isPreferredLanguageExposed={isPreferredLanguageExposed}
                      {...{ values, touched, errors:errorsRef, setFieldError }}

                    />
                    {showAddress && addresseEditable &&
                      <LocationInformationSection
                        {...{
                          values,
                          touched,
                          errors:errorsRef,
                          setFieldValue,
                          setValues,
                          setFieldError,
                          setFieldTouched,
                          handleBlur
                        }}
                        isAddressVerifyPass={isAddressVerifyPass}
                        setIsAddressVerifyPass={setIsAddressVerifyPass}
                        setFieldError={setFieldError}
                        isShowAddressVerifyTip={isShowAddressVerifyTip}
                        setShowAddressVerifyTip={setShowAddressVerifyTip}
                        isHasPrivateLocation={isHasPrivateLocation}
                        setIsHasPrivateLocation={setIsHasPrivateLocation}
                        userStatus={userStatus}
                        isEditLocation={isEditLocation}
                        setIsEditLocation={setIsEditLocation}
                        addressMandatory={addressMandatory}
                        resetLocationValues={whichCall => {
                          if (whichCall === 'verify') {
                            setIsDeleteOperate(false);
                          } else if (whichCall === 'delete') {
                            setIsDeleteOperate(true);
                            setValues({ ...values, ...nullLocationValues });
                            setFieldValue('lat', '', false);
                            setFieldValue('lon', '', false);
                          } else {
                            setValues({ ...values });
                          }
                        }}
                      />
                    }
                    {
                      !_.isEmpty(formatCountries) &&
                      <ContactInformationSection
                        {...{
                          values,
                          touched,
                          errors:errorsRef,
                          setFieldValue,
                          setFieldError,
                          setFieldTouched
                        }}
                        showMiniRequiredContactPathError={showMiniRequiredContactPathError}
                        minContactPath={minContactPath}
                        isAllFieldMandatory={isAllFieldMandatory}
                        setIscallOutMsgShow={setIscallOutMsgShow}
                        isEnableCountryCodeForMemberPortal={isEnableCountryCodeForMemberPortal}
                        countries={formatCountries}
                        selectedCountries={formatSelectedCountries}
                        hasUnsupportedCountrycode={hasUnsupportedCountrycode}
                        setHasUnsupportedCountrycode={setHasUnsupportedCountrycode}
                      />
                    }
                    {
                      iscallOutMsgShow &&
                      <div className="col-md-12">
                        <div className="row">
                          <div className="col-md-8">
                            <div className="alert alert-info mt-4" role="alert">
                              <FontAwesomeIcon
                                icon={faInfoCircle}
                                color={v('$blue-7')}
                                width="1em"
                                height="1em"
                              />
                              <div className="ml-2">
                                <div className="please-note-h">
                                  <I18N>ssp.myprofile.callOut.title</I18N>
                                </div>
                                <p className="mb-0">
                                  <I18N>ssp.myprofile.callOut.message</I18N>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    }
                    <AcceptInformation
                      isDeleteOperate={isDeleteOperate}
                      isAddressVerifyPass={isAddressVerifyPass}
                      validateField={validateField}
                      addressMandatory={addressMandatory}
                      setShowAddressVerifyTip={setShowAddressVerifyTip}
                      isHasPrivateLocation={isHasPrivateLocation}
                      {...{ values, errors, handleSubmit, isSubmitting, setFieldError, setSubmitting }}
                    />
                </form>
              )
              }}
            </Formik>
          </div>
        );
      }}
    </I18NConsumer>
  );
};

SignUpForm.propTypes = {
  geocodingAddresses: PropTypes.arrayOf(PropTypes.object).isRequired,
  createAccountForPublicOrg: PropTypes.func.isRequired,
  SMSSetting: PropTypes.shape(),
  phoneSetting: PropTypes.shape(),
  verifyLocation: PropTypes.func.isRequired,
  routerState: PropTypes.shape(),
  emailQuietTimeFrame: PropTypes.arrayOf(PropTypes.object),
  phoneQuietTimeFrame: PropTypes.arrayOf(PropTypes.object),
  smsQuietTimeFrame: PropTypes.arrayOf(PropTypes.object),
  slackQuietTimeFrame: PropTypes.arrayOf(PropTypes.object),
  signPrivateUserInfoError: PropTypes.shape(),
  fetchCountries: PropTypes.func.isRequired,
  countryData: PropTypes.shape(),
  fetchPublicProfile: PropTypes.func.isRequired,
  individualAccountId: PropTypes.number,
  preferredLanguage: PropTypes.string,
  preferredLanguageSettings: PropTypes.shape({
    languages: PropTypes.arrayOf(PropTypes.object),
    editable: PropTypes.bool,
    mandatory: PropTypes.bool,
    exposed: PropTypes.bool
  }),
};

SignUpForm.defaultProps = {
  SMSSetting: {},
  phoneSetting: {},
  routerState: null,
  emailQuietTimeFrame: null,
  phoneQuietTimeFrame: null,
  smsQuietTimeFrame: null,
  signPrivateUserInfoError: null,
  countryData: {},
  publicProfileData: {},
  individualAccountId: undefined,
  slackSetting: {},
  slackQuietTimeFrame: null,
  preferredLanguageSettings: {}
};

const mapStateToProps = state => ({
  geocodingAddresses: locationSelectors.selectLocationGeocodingAddresses(state),
  locationAddress: locationSelectors.selectLocationAddresses(state),
  addresses: signUpSelectors.selectSignPrivateUserInfoAddresses(state),
  firstName: signUpSelectors.selectSignPublicProfileInfoFirstName(state),
  lastName: signUpSelectors.selectSignPublicProfileInfoLastName(state),
  emailInfo: signUpSelectors.selectSignPublicProfileInfoEmailInfo(state),
  phoneInfo: signUpSelectors.selectSignPublicProfileInfoPhoneInfo(state),
  preferredLanguage: signUpSelectors.selectSignUpPublicProfileInfoLanguage(state),
  PhoneValidate: signUpSelectors.selectSignPhoneValidate(state),
  SMSInfo: signUpSelectors.selectSignPrivateUserInfoSMSInfo(state),
  SmsValidate: signUpSelectors.selectSignSmsValidate(state),
  phoneQuietTimeFrame: signUpSelectors.selectSignPhoneQuietTimeFrame(state),
  smsQuietTimeFrame: signUpSelectors.selectSignSmsQuietTimeFrame(state),
  emailQuietTimeFrame: signUpSelectors.selectSignEmailQuietTimeFrame(state),
  signPrivateUserInfoError: signUpSelectors.selectSignPrivateUserInfoError(state),
  countryData: signUpSelectors.selectSignUpCountryData(state),
  publicProfileId: signUpSelectors.selectSignUpPublicProfileId(state),
  individualAccountId: signUpSelectors.selectSignUpIndividualAccountId(state),
  countryCode: signUpSelectors.selectSignPublicProfileInfoCountryCode(state),
  slackInfo: signUpSelectors.selectSignPrivateUserInfoSlackInfo(state),
  slackQuietTimeFrame: signUpSelectors.selectSignSLackQuietTimeFrame(state),
  preferredLanguageSettings: signUpSelectors.selectSignUpLanguageData(state),
});

const mapDispatchToProps = dispatch => ({
  createAccountForPublicOrg: (data, callback, fetchInitData) =>
    dispatch(signUpActions.createAccountForPublicOrg.action({ data, callback, fetchInitData })),
  fetchPublicProfile: (data, callback) =>
    dispatch(signUpActions.fetchPublicProfile.action({ data, callback })),
  fetchLanguageSettings: (data, callback) =>
    dispatch(signUpActions.fetchLanguageSettings.action(data, callback)),
  verifyLocation: payload => dispatch(locationActions.verifyLocation(payload)),
  saveGeocodingAddress: address =>
    dispatch(locationActions.saveGeocodingAddress.action({ address })),
  fetchCountries: () => dispatch(signUpActions.fetchCountries.action()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SignUpForm);
